import React, { useState, useEffect } from 'react';
import { useAuth, API } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Users, Plus, Search, Mail, Phone, Home, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';
import { trackModuleVisit, trackEvent } from '@/lib/posthog';

const ResidentsPage = () => {
  const { user, selectedBuilding } = useAuth();
  const [residents, setResidents] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    unit_id: '',
    building_id: ''
  });

  // Track module visit on mount
  useEffect(() => {
    trackModuleVisit('residentes');
  }, []);

  useEffect(() => {
    if (selectedBuilding && selectedBuilding !== 'all') {
        fetchResidents();
        fetchUnits();
        setForm(prev => ({...prev, building_id: selectedBuilding}));
    }
  }, [selectedBuilding]);

  const fetchResidents = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/residents?building_id=${selectedBuilding}`);
      setResidents(res.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUnits = async () => {
    try {
      const res = await axios.get(`${API}/buildings/${selectedBuilding}/units`);
      setUnits(res.data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/residents`, form);
      toast.success('Residente invitado correctamente');
      // Track resident creation and invitation
      trackEvent('residente_created', { building_id: form.building_id, unidad: form.unit_id });
      trackEvent('residente_invited', { building_id: form.building_id });
      setCreateOpen(false);
      setForm({
        name: '',
        email: '',
        phone: '',
        unit_id: '',
        building_id: selectedBuilding
      });
      fetchResidents();
    } catch (error) {
      toast.error('Error al invitar residente');
    }
  };

  const handleDelete = async (id) => {
    if(!window.confirm('¿Estás seguro de eliminar este residente?')) return;
    try {
      // Get resident info before deleting for tracking
      const resident = residents.find(r => r.id === id);
      await axios.delete(`${API}/residents/${id}`);
      toast.success('Residente eliminado');
      // Track deactivation
      trackEvent('residente_deactivated', { building_id: resident?.building_id || selectedBuilding });
      fetchResidents();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const filteredResidents = residents.filter(r => 
    r.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getUnitName = (unitId) => {
    const unit = units.find(u => u.id === unitId);
    return unit ? `${unit.tower ? unit.tower + ' - ' : ''}${unit.number}` : 'Sin Asignar';
  };

  if (!selectedBuilding || selectedBuilding === 'all') {
      return (
        <DashboardLayout>
            <div className="flex flex-col items-center justify-center h-[60vh] text-center">
                <div className="h-16 w-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                    <Home className="h-8 w-8 text-slate-400" />
                </div>
                <h2 className="text-xl font-bold text-slate-900">Selecciona un conjunto</h2>
                <p className="text-slate-500 mt-2 max-w-md">
                    Para gestionar residentes, primero debes seleccionar un conjunto específico en la barra superior.
                </p>
            </div>
        </DashboardLayout>
      )
  }

  return (
    <DashboardLayout>
      <div className="space-y-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-heading font-bold text-slate-900">Directorio</h1>
            <p className="text-slate-500 mt-1 text-lg">Gestión de residentes y accesos</p>
          </div>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary hover:bg-primary/90 text-white rounded-xl shadow-lg px-6 h-12">
                <Plus className="h-5 w-5 mr-2" />
                Invitar Residente
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Registrar Nuevo Residente</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4 py-4">
                <div>
                  <Label>Nombre Completo</Label>
                  <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required placeholder="Ej: Maria Gonzalez" />
                </div>
                <div>
                  <Label>Correo Electrónico</Label>
                  <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required placeholder="maria@email.com" />
                </div>
                <div>
                  <Label>Teléfono</Label>
                  <Input type="tel" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="+57 300..." />
                </div>
                <div>
                  <Label>Asignar Unidad</Label>
                  <Select value={form.unit_id} onValueChange={(val) => setForm({...form, unit_id: val})}>
                    <SelectTrigger>
                        <SelectValue placeholder="Buscar apartamento..." />
                    </SelectTrigger>
                    <SelectContent>
                        {units.map(u => (
                            <SelectItem key={u.id} value={u.id}>
                                {u.tower ? `${u.tower} - ` : ''}{u.number}
                            </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" className="w-full bg-primary h-11">Enviar Invitación</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search Bar */}
        <div className="relative">
            <Search className="absolute left-3 top-3 h-5 w-5 text-slate-400" />
            <Input 
                className="pl-10 h-12 bg-white border-slate-200" 
                placeholder="Buscar por nombre, correo o unidad..." 
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
            />
        </div>

        {/* Residents List */}
        <div className="grid gap-4">
            {loading ? (
                <div className="text-center py-12">Cargando directorio...</div>
            ) : filteredResidents.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-xl border border-dashed border-slate-200">
                    <Users className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No se encontraron residentes</p>
                </div>
            ) : (
                filteredResidents.map(resident => (
                    <Card key={resident.id} className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:shadow-md transition-shadow">
                        <div className="flex items-center gap-4">
                            <div className="h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 font-bold text-lg">
                                {resident.name.charAt(0).toUpperCase()}
                            </div>
                            <div>
                                <h3 className="font-bold text-slate-900">{resident.name}</h3>
                                <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500 mt-1">
                                    <div className="flex items-center gap-1">
                                        <Mail className="h-3 w-3" /> {resident.email}
                                    </div>
                                    {resident.phone && (
                                        <div className="flex items-center gap-1">
                                            <Phone className="h-3 w-3" /> {resident.phone}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        
                        <div className="flex items-center gap-4 border-t sm:border-t-0 pt-4 sm:pt-0">
                            <div className="text-right mr-4">
                                <p className="text-xs text-slate-400 uppercase tracking-wider">Unidad</p>
                                <p className="font-semibold text-primary">{getUnitName(resident.unit_id)}</p>
                            </div>
                            <div className="flex gap-2">
                                <Button variant="ghost" size="icon" className="text-red-500 hover:bg-red-50" onClick={() => handleDelete(resident.id)}>
                                    <Trash2 className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    </Card>
                ))
            )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ResidentsPage;
